"use client";

/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from "react";
import {
  useState, useMemo, useContext, createContext, useEffect, useCallback,
} from "react";
import { usePathname, useSearchParams, useRouter } from "next/navigation";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import SharedComponents, { Avatar, VideoReadyUpModal } from "@audacia-hq/shared/components";
import { Subject } from "rxjs";
import dynamic from "next/dynamic";
import { useVideo } from "@audacia-hq/shared/contexts";

import { Expert } from "../models/expert";
import { ConsultationType } from "../models/global";
import { Consultation, TrialConsultation, TrialOffer } from "../models/consultation";
import ConsultationModal from "../components/consultations/ConsultationModal";
import { stopLivechat } from "../services/api";
import { GET_VIDEO_ROOM } from "../graphql/query";
import { useLazyFetcher } from "../hooks/useClientFetcher";

import { useExpert } from "./ExpertContext";
import { WSMessage, useWS } from "./WSContext";
import { useMember } from "./MemberContext";
import { useQueue } from "./QueueContext";
import { useAuth } from "./AuthContext";

const TrialOfferForm = dynamic(() => import("../components/consultations/TrialOfferForm"));

interface ConsultationContextType {
  openConsultationModal: (type: ConsultationType) => (expert: Expert) => void;
  ongoingConsultation?: Partial<Consultation>;
  ongoingTrialConsultation?: Partial<TrialConsultation>;
  consultationUpdateFeed: Subject<Partial<Consultation | TrialConsultation>>;
}

const ConsultationContext = createContext<ConsultationContextType>({
  openConsultationModal: () => () => ({}),
  consultationUpdateFeed: undefined,
});

const ConsultationProvider: React.FC<{children: React.ReactNode;}> = ({ children }) => {
  const [consultationModal, setConsultationModal] = useState<undefined | ConsultationType>();
  const [expertModal, setExpertModal] = useState<Expert>();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const ws = useWS();
  const { user } = useAuth();
  const { getExperts, expertUpdateFeed } = useExpert();
  const { member, pendingActions, refreshPendingActions } = useMember();
  const { rawMemberQueues } = useQueue();
  const [noModal, setNoModal] = useState<boolean>(false); // video readyup modal, for now
  const {
    videoRoom, initVideoRoom, showReadyUpModal, closeVideoRoom,
  } = useVideo();
  const [getVideoRoom] = useLazyFetcher(GET_VIDEO_ROOM, { authenticated: true });
  const [displayModal, setDisplayModal] = useState<boolean>(false);

  const { t } = useTranslation("consultations");

  const [queuedExperts, setQueuedExperts] = useState<Expert[]>([]);
  const [trialOffer, setTrialOffer] = useState<TrialOffer>(null);
  const [consultationConfirm, setConsultationConfirm] = useState<Consultation>();
  const [ongoingConsultation, setOngoingConsultation] = useState<Partial<Consultation>>();
  const [ongoingTrialConsultation, setOngoingTrialConsultation] = useState<Partial<TrialConsultation>>();
  const [consultationUpdateFeed] = useState<
    Subject<Partial<Consultation | TrialConsultation>>
    >(new Subject<Partial<Consultation | TrialConsultation>>());

  const updateExpertModal = useCallback((expertUid, consultation: ConsultationType) => {
    getExperts([expertUid]).then((experts) => {
      setExpertModal(experts[0]);
      setConsultationModal(consultation);
    });
  }, [getExperts]);

  useEffect(() => {
    const noModalParam = searchParams.get("noModal");
    setNoModal(!!noModalParam);
  }, [searchParams]);

  useEffect(() => {
    if (pathname?.startsWith("/packs")) {
      setConsultationModal(undefined);
      return;
    }
    const cm = localStorage.getItem("consultationModal");
    if (!cm) return;

    const scm = cm.split("#");
    updateExpertModal(scm[1], scm[0] as ConsultationType);
  }, [pathname]);

  useEffect(() => {
    const sub = expertUpdateFeed.subscribe((expertUids) => {
      const cm = localStorage.getItem("consultationModal");
      if (!cm) return;

      const scm = cm.split("#");
      if (expertUids.find((expertUid) => expertUid === scm[1])) { updateExpertModal(scm[1], scm[0] as ConsultationType); }
    });
    return () => sub.unsubscribe();
  }, [expertUpdateFeed, updateExpertModal]);

  useEffect(() => {
    if (Object.keys(rawMemberQueues).length > 0) getExperts(Object.keys(rawMemberQueues)).then((res) => setQueuedExperts(res));
  }, [rawMemberQueues]);

  useEffect(() => { // "fix" for headlessUI scroll issue on iOS
    if (showReadyUpModal) {
      window.document.body.style.scrollBehavior = "auto";
      setTimeout(() => { // similar to nextTick in vuejs
        setDisplayModal(!!showReadyUpModal);
      });
    }
  }, [showReadyUpModal]);

  useEffect(() => {
    if (displayModal) {
      setTimeout(() => {
        window.document.body.style.scrollBehavior = null;
      });
    }
  }, [displayModal]);

  const handleNewConsultation = React.useCallback((consultation: Consultation) => {
    if (consultation.channel === "WRITTEN") return;
    const queue = rawMemberQueues[consultation.expertUid];
    if (consultation.channel === "LIVECHAT"
      && queue && dayjs().diff(dayjs(queue.queuedAt), "minute") >= 1
      && (queue.items.length > 0 && !queue.items[0].startedAt)
      && pathname !== `/livechat/${consultation.channelRefUid}`
    ) {
      setConsultationConfirm(consultation);
    } else {
      setOngoingConsultation(consultation);
      consultationRedirect(consultation);
    }
    closeConsultationModal();
  }, [rawMemberQueues]);

  const handleTrialOffer = (eventOffer: TrialOffer) => {
    if (eventOffer.status === "PENDING") setTrialOffer(eventOffer);
    if (eventOffer.status === "EXPIRED") setTrialOffer(undefined);
  };

  const resetConsultation = () => {
    setOngoingConsultation(undefined);
    setOngoingTrialConsultation(undefined);
    setConsultationConfirm(undefined);
  };

  const toastFailedConsultation = ({ failReason }) => {
    const failedBy = failReason?.split("_", 1)?.[0];
    switch (failedBy) {
      case "EXPERT":
        toast.error(t("messages.consultationFailedByExpert"));
        break;
      case "MEMBER":
        toast.error(t("messages.consultationCanceledByMember"));
        break;
      default:
        toast.error(t("messages.consultationCanceled"));
    }
  };

  const handleConsultationEvents = React.useCallback((msg: WSMessage) => {
    // TODO: resetConsultation with some finished event (for example: member.consultation.expired, canceled, finished) ?
    switch (msg.event) {
      case "ws.reconnected":
        refreshPendingActions();
        break;
      case "member.consultation.requested":
        handleNewConsultation(JSON.parse(msg.payload));
        break;
      case "member.consultation.started":
        setOngoingConsultation(JSON.parse(msg.payload));
        break;
      case "member.consultation.finished":
        toast.success(t("messages.consultationFinished"));
        resetConsultation();
        handleFinishedConsultation(JSON.parse(msg.payload));
        break;
      case "member.consultation.failed":
        resetConsultation();
        toastFailedConsultation(JSON.parse(msg.payload));
        consultationUpdateFeed.next(JSON.parse(msg.payload));
        break;
      case "member.consultation.canceled":
        resetConsultation();
        toast.warning(t("messages.consultationCanceled"));
        consultationUpdateFeed.next(JSON.parse(msg.payload));
        break;
      case "member.consultation.expired":
        resetConsultation();
        toast.warning(t("messages.consultationExpired"), { autoClose: false });
        consultationUpdateFeed.next(JSON.parse(msg.payload));
        break;
      case "member.noCreditAvailable":
        toast.warning(t("member:messages.noCreditAvailable"), { autoClose: false });
        break;
      case "member.trial.offer":
        handleTrialOffer(JSON.parse(msg.payload));
        break;
      case "member.trialConsultation.requested":
        consultationRedirect(JSON.parse(msg.payload));
        setOngoingTrialConsultation(JSON.parse(msg.payload));
        break;
      case "member.trialConsultation.started":
        setOngoingTrialConsultation(JSON.parse(msg.payload));
        break;
      case "member.trialConsultation.finished":
        setOngoingTrialConsultation(undefined);
        refreshPendingActions();
        router.push("/");
        break;
      case "member.trialConsultation.failed":
        setOngoingTrialConsultation(undefined);
        toastFailedConsultation(JSON.parse(msg.payload));
        consultationUpdateFeed.next(JSON.parse(msg.payload));
        break;
      case "member.trialConsultation.expired":
        setOngoingTrialConsultation(undefined);
        toast.warning(t("messages.consultationExpired"), { autoClose: false });
        consultationUpdateFeed.next(JSON.parse(msg.payload));
        break;
      default:
        break;
    }
  }, [handleNewConsultation, consultationUpdateFeed]);

  useEffect(() => {
    const sub = ws.subscribe((msg) => handleConsultationEvents(msg as WSMessage));
    return () => sub.unsubscribe();
  }, [ws, handleConsultationEvents]);

  useEffect(() => {
    if (!pendingActions) return;
    if (pendingActions.onGoingConsultation) {
      setOngoingConsultation(pendingActions.onGoingConsultation);
      handleNewConsultation(pendingActions.onGoingConsultation);
    }
    if (pendingActions.trialConsultation && pendingActions.trialConsultation.status !== "PENDING_FEEDBACK") {
      setOngoingTrialConsultation(pendingActions.trialConsultation);
      consultationRedirect(pendingActions.trialConsultation);
    }
    if (pendingActions.trialOffer) setTrialOffer(pendingActions.trialOffer);
    if (pendingActions.trialOffer === null && trialOffer !== null) setTrialOffer(undefined);
  }, [pendingActions]);

  useEffect(() => {
    if (!user) resetConsultation();
  }, [user]);

  useEffect(() => {
    if (!ongoingConsultation && !ongoingTrialConsultation) return;
    const ongoing = ongoingConsultation || ongoingTrialConsultation;
    if ((ongoing?.channel === "VIDEO") && !videoRoom) {
      getVideoRoom({ variables: { videoRoomUid: ongoing?.channelRefUid } })
        .then((res) => {
          initVideoRoom(res.data.videoRoom);
        });
    }
  }, [ongoingConsultation, ongoingTrialConsultation]);

  const consultationRedirect = (consultation: Consultation | TrialConsultation) => {
    if (consultation.channel === "LIVECHAT" && pathname !== `/livechat/${consultation.channelRefUid}`) {
      router.push(`/livechat/${consultation.channelRefUid}`);
    }
    if (consultation.channel === "PHONE" && (consultation as Consultation).consultationUid && pathname !== `/phone/${(consultation as Consultation).consultationUid}`) {
      router.push(`/phone/${(consultation as Consultation).consultationUid}`);
    }
    if (consultation.channel === "PHONE" && (consultation as TrialConsultation).trialConsultationUid && pathname !== `/phone/${(consultation as TrialConsultation).trialConsultationUid}`) {
      router.push(`/phone/${(consultation as TrialConsultation).trialConsultationUid}`);
    }
  };

  const handleFinishedConsultation = (consultation: Consultation) => {
    if (consultation.channel !== "WRITTEN") router.push(`/my-consultations/${consultation.consultationUid}`);
  };

  const openConsultationModal = (type: ConsultationType) => (expert: Expert) => {
    setExpertModal(expert);
    setConsultationModal(type);
    localStorage.setItem("consultationModal", `${type}#${expert.expertUid}`);
  };

  const closeConsultationModal = () => {
    setExpertModal(undefined);
    setConsultationModal(undefined);
    localStorage.removeItem("consultationModal");
  };

  const value = useMemo(
    () => (
      {
        openConsultationModal,
        ongoingConsultation,
        ongoingTrialConsultation,
        consultationUpdateFeed,
      }),
    [openConsultationModal, ongoingConsultation, ongoingTrialConsultation, consultationUpdateFeed],
  );

  return (
    <ConsultationContext.Provider value={value}>
      {consultationModal && expertModal && (
        <ConsultationModal
          type={consultationModal}
          expert={expertModal}
          ongoingConsultation={ongoingConsultation}
          onClose={closeConsultationModal}
        />
      )}
      {trialOffer && (
        <SharedComponents.Modal title={t("trialConsultation.offerTitle")} show>
          <TrialOfferForm trialOffer={trialOffer} onSubmit={() => setTrialOffer(undefined)} />
        </SharedComponents.Modal>
      )}
      <SharedComponents.AlertModal
        open={consultationConfirm !== undefined}
        title={t("queue.consultationConfirm.title")}
        validateLabel={t("common:agree")}
        cancelLabel={t("common:refuse")}
        variant="info"
        playsong
        disableClosingOverlayEvent
        onValidate={() => {
          consultationRedirect(consultationConfirm);
          setOngoingConsultation(consultationConfirm);
          setConsultationConfirm(undefined);
        }}
        onClose={() => {
          if (consultationConfirm.channel === "LIVECHAT") stopLivechat(({ variables: { livechatRoomUid: consultationConfirm.channelRefUid } }));
          setConsultationConfirm(undefined);
        }}
      >
        {t("queue.consultationConfirm.explaination", {
          expert: queuedExperts.find((e) => e.expertUid === consultationConfirm?.expertUid)?.pseudo,
          channel: consultationConfirm?.channel,
        })}
      </SharedComponents.AlertModal>
      {displayModal && showReadyUpModal && !noModal && (
        <VideoReadyUpModal
          videoRoom={showReadyUpModal}
          userPlaceholder={(
            <Avatar
              uid={member.memberUid}
              alt={member.profile?.pseudo || "M"}
              name={member.profile?.pseudo || "M"}
              className="rounded-full border-2 border-gray-200 aspect-square"
              height="30%"
            />
          )}
          onReady={() => {
            router.push(`/video/${showReadyUpModal.videoRoomUid}`);
          }}
          showCancel
          helpLink="/faq?target=videos&noModal=true"
        />
      )}
      {children}
    </ConsultationContext.Provider>
  );
};

const useConsultation = () => useContext(ConsultationContext);

export default ConsultationContext;
export {
  ConsultationProvider, useConsultation,
};
